import Info from '@mui/icons-material/Info';
import { Grid, Typography } from '@mui/material';
import html2canvas from 'html2canvas';
import React, { useRef } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import EnhancedTable from '../../../../../../lib/designSystem/Table';
import { CustomTooltip } from '../../../../../../lib/designSystem/TooltipTable';
import { LightTooltip } from '../../../../../../lib/designSystem/WhiteTooltip';
import { formatDate, formatMoney } from '../../../../../../lib/functions/utils';
import { generateCompanyReportPDF } from './components/exportCompany';

interface Props {
  response: any;
  lastUpdate?: Date;
}

export const boletinesLabels: Record<
  string,
  {
    label: string;
    dataHeaders: { key: string; label: string; type?: string }[];
  }
> = {
  boled: {
    label: 'Boletín Digtal',
    dataHeaders: [
      { key: 'chequeOperatioNumber', label: 'Número de Cheque' },
      { key: 'debtType', label: 'Tipo de Deuda' },
      { key: 'documentType', label: 'Tipo de Documento' },
      { key: 'unpaidAmount', label: 'Monto Insoluto', type: 'money' },
      {
        key: 'equifaxRegisterDate',
        label: 'Registro en Boletín',
        type: 'date',
      },
      { key: 'expirationDate', label: 'Fecha de Expiración', type: 'date' },
      {
        key: 'justificationDate',
        label: 'Fecha de Justificación',
        type: 'date',
      },
      {
        key: 'justificationDescription',
        label: 'Descripción de Justificación',
      },
      { key: 'libradorName', label: 'Nombre del Librador' },
      { key: 'localidadName', label: 'Nombre de la Localidad' },
      { key: 'marketCode', label: 'Código de Mercado' },
      { key: 'marketDescription', label: 'Descripción de Mercado' },
      { key: 'moneyCode', label: 'Código de Moneda' },
      { key: 'typeReason', label: 'Tipo de Razón' },
      { key: 'visibilityDate', label: 'Fecha de Visibilidad', type: 'date' },
    ],
  },
  bolab: {
    label: 'Boletín Laboral',
    dataHeaders: [
      { key: 'boletinNumber', label: 'Número de Boletín' },
      { key: 'boletinPage', label: 'Página de Boletín' },
      { key: 'delinquencyAmount', label: 'Monto de Deuda', type: 'money' },
      { key: 'delinquencyCotizacionNumber', label: 'Número de Cotización' },
      { key: 'delinquencyMonthsNumber', label: 'Número de Meses de Deuda' },
      { key: 'infractionNumber', label: 'Número de Infracción' },
      { key: 'injuryType', label: 'Tipo de Infracción' },
      { key: 'institutionName', label: 'Nombre de la Institución' },
      { key: 'resolutionNumber', label: 'Número de Resolución' },
      { key: 'resolutionYear', label: 'Año de Resolución' },
      { key: 'supervisorNumber', label: 'Número de Supervisor' },
      { key: 'supervisorRegion', label: 'Región de Supervisor' },
    ],
  },
  bolcom: {
    label: 'Boletín Comercial',
    dataHeaders: [
      { key: 'boletinPage', label: 'Página de Boletín' },
      { key: 'chequeOperatioNumber', label: 'Número de Cheque' },
      { key: 'creditType', label: 'Tipo de Crédito' },
      { key: 'debtType', label: 'Tipo de Deuda' },
      { key: 'documentType', label: 'Tipo de Documento' },
      { key: 'unpaidAmount', label: 'Monto Insoluto', type: 'money' },
      { key: 'expirationDate', label: 'Fecha de Expiración', type: 'date' },
      {
        key: 'justificationDate',
        label: 'Fecha de Justificación',
        type: 'date',
      },
      {
        key: 'justificationDescription',
        label: 'Descripción de Justificación',
      },
      { key: 'libradorName', label: 'Nombre del Librador' },
      { key: 'localidadName', label: 'Nombre de la Localidad' },
      { key: 'marketCode', label: 'Código de Mercado' },
      { key: 'marketDescription', label: 'Descripción de Mercado' },
      { key: 'moneyCode', label: 'Código de Moneda' },
      { key: 'notarioName', label: 'Nombre del Notario' },
      { key: 'reasonType', label: 'Tipo de Razón' },
      { key: 'registerDate', label: 'Fecha de Registro', type: 'date' },
    ],
  },
  icom: {
    label: 'Informe Complementario de Morosidades',
    dataHeaders: [
      { key: 'chequeOperatioNumber', label: 'Número de Cheque' },
      { key: 'creditType', label: 'Tipo de Crédito' },
      { key: 'debtType', label: 'Tipo de Deuda' },
      { key: 'documentType', label: 'Tipo de Documento' },
      { key: 'unpaidAmount', label: 'Monto Insoluto', type: 'money' },
      { key: 'expirationDate', label: 'Fecha de Expiración', type: 'date' },
      { key: 'libradorName', label: 'Nombre del Librador' },
      { key: 'localidadName', label: 'Nombre de la Localidad' },
      { key: 'marketCode', label: 'Código de Mercado' },
      { key: 'marketDescription', label: 'Descripción de Mercado' },
      { key: 'moneyCode', label: 'Código de Moneda' },
      { key: 'reasonType', label: 'Tipo de Razón' },
    ],
  },
};

const CompanyReport: React.FC<Props> = ({ response, lastUpdate }) => {
  const data = response.data;
  const debt = data.summary?.debt || {};

  const boletines = Object.keys(boletinesLabels).map((key) => ({
    key,
    label: boletinesLabels[key],
    data: debt[key],
  }));

  const chartRef = useRef<HTMLDivElement>(null);

  const chartData =
    data.summary?.riskScore?.historicalRate
      .map((item: any) => ({
        name: item.name.replace(/-/g, '/'),
        value: item.value,
      }))
      .reverse() || []; // Reverse to show oldest to newest

  const handleExport = async () => {
    const element = chartRef.current;
    if (!element) return;

    const canvas = await html2canvas(element, {
      backgroundColor: null,
      useCORS: true,
    });
    const imgData = chartData.length > 0 ? canvas.toDataURL('image/png') : null;
    generateCompanyReportPDF(response, lastUpdate, imgData);
  };

  return (
    <div>
      <div>
        <div
          ref={chartRef}
          style={{
            width: 800,
            height: 200,
            position: 'absolute',
            left: -9999,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#ccc" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#0F23DF" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Informe Buro
        </Typography>
        <Typography
          variant="body2"
          style={{ cursor: 'pointer', color: 'blue', marginLeft: '10px' }}
          onClick={handleExport}
        >
          Exportar
        </Typography>
      </div>
      <Grid container spacing={2}>
        {/* Datos Generales */}
        <Grid item xs={6}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Datos Generales
          </Typography>
          <Typography>
            <strong>RUT:</strong> {response.identifier}
          </Typography>
          <Typography>
            <strong>Razón Social:</strong>{' '}
            <LightTooltip
              title={
                <Typography variant="body1" padding={1}>
                  {data.companySummary.businessName}
                </Typography>
              }
            >
              {data.companySummary.businessName.length > 50
                ? data.companySummary.businessName.slice(0, 50) + '...'
                : data.companySummary.businessName}
            </LightTooltip>
          </Typography>
          <Typography>
            <strong>Fecha de Inicio:</strong>{' '}
            {formatDate(data.companySummary.startDate)}
          </Typography>
          <Typography>
            <strong>Tamaño de Empresa:</strong>{' '}
            {data.companySummary.companySize}
          </Typography>
          <Typography>
            <strong>Colaboradores:</strong> {data.companySummary.collaborators}
          </Typography>
          <Typography>
            <strong>Sin Deudas (Everclean):</strong>{' '}
            {data.companySummary.everclean === '1' ? 'Sí' : 'No'}
          </Typography>
        </Grid>
        {/* Resumen de Deudas y Protestos */}
        <Grid item xs={6} md={6}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Resumen de Deudas y Protestos
          </Typography>
          <Typography>
            <strong>Protestos:</strong>{' '}
            {data.summary?.debt?.protests?.documentCount}
          </Typography>
          <Typography>
            <strong>Multas/Infracciones:</strong>{' '}
            {data.summary?.debt?.totalMultaeInfraccionLaboralPrevisional}
          </Typography>
          <Typography>
            <strong>Acceso Cámara de Comercio:</strong>{' '}
            {data.summary?.debt?.accessCamaraComercio}
          </Typography>
          <Typography>
            <strong>Boletines:</strong>{' '}
            {formatMoney(
              Number(data.summary?.debt?.bolab?.amount) +
                Number(data.summary?.debt?.bolcom?.amount) +
                Number(data.summary?.debt?.boled?.amount) +
                Number(data.summary?.debt?.icom?.amount),
            )}{' '}
            (
            {data.summary?.debt?.bolab?.documentCount +
              data.summary?.debt?.bolcom?.documentCount +
              data.summary?.debt?.boled?.documentCount +
              data.summary?.debt?.icom?.documentCount}
            )
          </Typography>
        </Grid>

        {/* Bienes */}
        <Grid item xs={6} md={6}>
          {' '}
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Bienes
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography>
                <strong>Propiedades:</strong>{' '}
                {data.tributarySummary?.realState?.propertiesCount}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>
                <strong>Valor Propiedades:</strong>{' '}
                {data.tributarySummary?.realState
                  ? formatMoney(
                      Number(
                        data.tributarySummary?.realState
                          .totalAssesmentAmountPesos,
                      ),
                    )
                  : '0'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>
                <strong>Vehículos:</strong> {data.summary?.vehiclesNumber}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>
                <strong>Valor Vehículos:</strong>{' '}
                {formatMoney(data.summary?.vehiclesValuatio)}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* Riesgo */}
        <Grid item xs={6}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Indicadores de Riesgo
          </Typography>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography>
              <strong>Score de Riesgo:</strong> {data.summary?.riskScore?.score}
            </Typography>
            <LightTooltip
              title={CustomTooltip(
                data.summary?.riskScore?.historicalRate,
                'Fecha',
                'Score',
              )}
              placement="left"
            >
              <Typography
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                Ver Histórico{' '}
                <Info style={{ marginLeft: '4px', fontSize: '14px' }} />
              </Typography>
            </LightTooltip>
          </div>

          <Typography>
            <strong>Acreditaciones:</strong> {data.summary?.accreditationsFlag}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Actividades
          </Typography>
          <EnhancedTable
            data={data.companySummary.activities}
            dataHeaders={[
              { key: 'activityCode', label: 'Actividad' },
              { key: 'description', label: 'Tipo de Actividad' },
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Socios
          </Typography>
          <EnhancedTable
            data={data.companySummary.relatedPartners}
            dataHeaders={[
              { key: 'namePartner', label: 'Nombre' },
              { key: 'rutPartner', label: 'RUT' },
              { key: 'partnershipCount', label: 'Participaciones' },
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Sociedades
          </Typography>
          <EnhancedTable
            data={data.companySummary.mainPartners}
            dataHeaders={[
              { key: 'businessName', label: 'Razón Social' },
              { key: 'rutOfTheCompany', label: 'RUT' },
              { key: 'partnerCount', label: 'Cantidad de Socios' },
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Ejecutivos
          </Typography>
          <EnhancedTable
            data={data.companySummary.executives}
            dataHeaders={[
              { key: 'name', label: 'Nombre' },
              { key: 'email', label: 'Email', type: 'copyable' },
              { key: 'phoneNumber', label: 'Teléfono' },
              { key: 'roleDescription', label: 'Rol' },
              {
                key: 'executiveInformationDate',
                label: 'Registro',
                type: 'date',
              },
            ]}
          />
        </Grid>
        {boletines.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Boletines
            </Typography>
            {boletines.map((boletin, idx) => (
              <Grid item xs={12} key={idx} style={{ marginBottom: '16px' }}>
                <Typography variant="subtitle1" gutterBottom>
                  {boletin.label.label}
                </Typography>
                <Typography>
                  <strong>Monto Total:</strong>{' '}
                  {formatMoney(boletin.data.amount)}
                </Typography>
                <Typography>
                  <strong>Documentos:</strong> {boletin.data.documentCount}
                </Typography>
                {boletin.data.documents?.length > 0 ? (
                  <EnhancedTable
                    data={boletin.data.documents}
                    dataHeaders={boletin.label.dataHeaders}
                  />
                ) : (
                  <Typography variant="body2">
                    Sin detalles disponibles.
                  </Typography>
                )}
              </Grid>
            ))}
          </Grid>
        )}
        {/* Consultas de Terceros */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Consultas de Terceros
          </Typography>
          <EnhancedTable
            data={data.companySummary.consultsByOthers}
            dataHeaders={[
              { key: 'businessName', label: 'Empresa Consultante' },
              { key: 'date', label: 'Fecha', type: 'date' },
              { key: 'report', label: 'Tipo de Reporte' },
            ]}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default CompanyReport;
